<template>
  <div class="cf-card cf-flex cf-flex--column cf-flex--gap-16">
    <div
      :class="[{
        'cf-grid cf-grid--2-grid-column-left-large': summaryCardDetails.hasImgLogo,
        'cf-flex cf-flex--row cf-flex--gap-16': !summaryCardDetails.hasImgLogo
      }]"
    >
      <div
        v-if="!loyalty.isMembershipTierStatus"
        class="cf-flex cf-flex--align-center"
      >
        <cf-loyalty-progress-ring />
      </div>

      <div class="cf-flex cf-flex--column cf-flex--gap-8">
        <p
          v-if="summaryCardDetails.header"
          class="cf-subtitle cf-subtitle--2"
        >
          {{ summaryCardDetails.header }}
        </p>

        <h1 class="cf-title cf-title--1">
          {{ summaryCardDetails.subheader }}
        </h1>

        <div class="cf-flex cf-flex--column cf-flex--gap-8">
          <p
            v-if="summaryCardDetails.descriptionText"
            class="cf-subtitle cf-subtitle--2"
          >
            {{ summaryCardDetails.descriptionText }}
          </p>

          <h4
            v-if="summaryCardDetails.availableRewardsText"
            class="cf-title cf-title--4 cf-subtitle--brand-primary"
          >
            {{ summaryCardDetails.availableRewardsText }}
          </h4>

          <p
            v-if="!hideLearnMore"
            class="cf-link cf-link--heavy"
            @click="learnMoreClickHandler"
          >
            Learn More
          </p>
        </div>
      </div>

      <img
        v-if="summaryCardDetails.hasImgLogo && !loyalty.isMultiTiered && loyalty.isMembershipTierStatus"
        class="cf-image cf-image--logo-smaller cf-justify-self-end"
        :alt="summaryCardDetails.imageAlt"
        :src="summaryCardDetails.imgSrc"
        onerror="this.src='/images/image-not-found.png';"
      >
    </div>

    <button
      v-if="!loyalty.isEnrolled"
      class="button button--auto-width button--primary"
      type="button"
      @click="enrollInLoyalty"
    >
      {{ content.Loyalty.metadata.unenrolledButtonCta }}
    </button>

    <div class="cf-flex cf-flex--row">
      <div class="cf-flex cf-flex--row cf-flex--gap-16 cf-mobile-column cf-full-width">
        <router-link
          v-if="displayRewardsActivityLink"
          class="cf-button cf-button--primary cf-button--lg cf-button--auto"
          :to="{ name: 'loyaltyActivity' }"
        >
          <span class="cf-icon cf-icon--white far fa-list" />

          View Rewards Activities
        </router-link>

        <button
          v-if="isScanToCheckInEnabled"
          class="cf-button cf-button--tertiary cf-button--lg cf-button--auto"
          type="button"
          @click="$openModal('loyaltyScanModal')"
        >
          <span class="cf-icon far fa-qrcode" />

          {{ loyaltyScanModalCtaContent }}
        </button>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import events from '@/services/events';

  import CfLoyaltyProgressRing from '@/view/components/shared/cf-loyalty-progress-ring/index.vue';


  export default {
    name: 'LoyaltyRewardsPointsSummary',

    components: {
      CfLoyaltyProgressRing
    },

    props: {
      hideLearnMore: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('offers', ['loyalty', 'rewardCount']),

      isPointsGiftingEnabled() {
        const hasPrimaryCurrency = this.loyalty.primaryLedger?.currency;

        if (hasPrimaryCurrency) {
          const { isActive = false, minPointsPerGift = 1 } = this.loyalty.primaryLedger.currency.pointGiftPolicy || { isActive: false, minPointsPerGift: 1 };

          return isActive && this.loyalty.currentBalance >= minPointsPerGift;
        }

        return false;
      },

      pointsGifitingCta() {
        return this.content.Loyalty.metadata.giftPointsButtonCta;
      },

      displayRewardsActivityLink() {
        const { memberActivityUIEnabled } = this.merchant.options.loyaltyOptions;

        return memberActivityUIEnabled && this.merchant.features['loyalty-activity-tab-enabled'];
      },

      isScanToCheckInEnabled() {
        // Note: loyaltyCardCheckInEnabled sets the scan to earn cta button within the card as opposed to the <scan-to-earn-cta/>
        const { loyaltyCheckInEnabled, loyaltyCardCheckInEnabled, loyaltyReceiptScanningEnabled } = this.merchant.options.loyaltyOptions;

        return this.loyalty.isEnrolled && loyaltyCardCheckInEnabled && (loyaltyCheckInEnabled || loyaltyReceiptScanningEnabled);
      },

      loyaltyScanModalCtaContent() {
        const { loyaltyCheckInEnabled, loyaltyReceiptScanningEnabled } = this.merchant.options.loyaltyOptions;

        let scanCta = '';

        if (loyaltyCheckInEnabled) scanCta = this.content.ScanToCheckInLoyaltyScreen.metadata.scanToCheckInCta;

        if (loyaltyReceiptScanningEnabled) scanCta = this.content.ScanToEarnLoyaltyScreen.metadata.scanToEarnCta;

        if (loyaltyCheckInEnabled && loyaltyReceiptScanningEnabled) scanCta = this.content.ScanToCheckInAndEarnLoyaltyScreen.metadata.scanToEarnCta;

        return scanCta;
      },

      summaryCardDetails() {
        const obj = {
          header: this.content.Loyalty.metadata.unenrolledTitle,
          subheader: null,
          descriptionText: this.content.Loyalty.metadata.unenrolledSubtitle,
          hasImgLogo: !!this.content.LoyaltyEnrollment.metadata.imageUrl,
          imgSrc: this.content.LoyaltyEnrollment.metadata.imageUrl,
          imageAlt: this.merchant.name
        };

        if (this.loyalty.isEnrolled) {
          obj.header = null;
          obj.subheader = `${this.loyalty.currentBalance} ${this.loyalty.currency.pluralNameCapitalCase}`;
          obj.descriptionText = this.loyalty.pointsUntilNextReward
            ? `${this.loyalty.pointsUntilNextReward} ${this.loyalty.currency.pluralName} until your next reward.`
            : "You've unlocked every reward!";
          obj.availableRewardsText = `${this.rewardCount} Reward${this.rewardCount !== 1 ? 's' : ''} Available`;
          obj.hasImgLogo = false;

          if (this.loyalty.tier?.current?.name) {
            obj.header = this.loyalty.tier.current.name;
            obj.subheader = `${this.loyalty.currentBalance} ${this.loyalty.currency.pluralNameCapitalCase}`;
          }

          if (this.loyalty.isMembershipTierStatus) {
            obj.header = null;
            obj.subheader = this.loyalty.tier.title;
            obj.descriptionText = this.loyalty.tier.description;
            obj.hasImgLogo = !!this.loyalty.tier.current?.badgeDefaultUrl;
            obj.imgSrc = this.loyalty.tier.current?.badgeDefaultUrl;
            obj.imageAlt = this.loyalty.tier.current.name;
          }
        }

        return obj;
      }
    },

    methods: {
      enrollInLoyalty() {
        events.emit('enroll-in-loyalty:open', {
          forceOpen: true,
          skipInitialContent: true
        });
      },

      learnMoreClickHandler() {
        this.$router.push({
          name: 'faqs',
          params: { merchantId: this.$route.params.merchantId }
        });
      }
    }
  };
</script>
